import { getRefreshToken, getToken, setToken } from './auth'
import { publicApi } from './api'

export type WSStatus = 'idle' | 'connecting' | 'open' | 'closing' | 'closed'

type Handler = (payload: any) => void

class Emitter {
  private map = new Map<string, Set<Handler>>()
  on(type: string, cb: Handler) {
    const set = this.map.get(type) ?? new Set<Handler>()
    set.add(cb)
    this.map.set(type, set)
    return () => this.off(type, cb)
  }
  off(type: string, cb: Handler) {
    this.map.get(type)?.delete(cb)
  }
  emit(type: string, payload: any) {
    this.map.get(type)?.forEach((cb) => cb(payload))
  }
}

export type WSOptions = {
  path: string // e.g. '/ws/inbox/' or `/ws/threads/${id}/`
  authAsQuery?: boolean // append ?token=...
}

function stripTrailingSlash(input: string): string {
  if (!input) return ''
  return input.endsWith('/') ? input.slice(0, -1) : input
}

function toWsBaseFromLocation(): string {
  const override = import.meta?.env?.VITE_WS_BASE as string | undefined
  if (override) return stripTrailingSlash(override)

  const httpBase = import.meta?.env?.VITE_API_BASE_URL as string | undefined
  if (httpBase) {
    try {
      const url = new URL(httpBase, window.location.href)
      const scheme = url.protocol === 'https:' ? 'wss:' : 'ws:'
      return `${scheme}//${url.host}${stripTrailingSlash(url.pathname || '')}`
    } catch {
      // fall through to location-based fallback
    }
  }

  const { protocol, host } = window.location
  const wsScheme = protocol === 'https:' ? 'wss:' : 'ws:'
  return `${wsScheme}//${host}`
}

function cleanPath(p: string): string {
  if (!p) return '/'
  return p.startsWith('/') ? p : `/${p}`
}

function getJwtExpMs(token: string): number | null {
  try {
    const parts = String(token || '').split('.')
    if (parts.length < 2) return null
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    const exp = Number(payload?.exp)
    if (!Number.isFinite(exp) || exp <= 0) return null
    return exp * 1000
  } catch {
    return null
  }
}

function isJwtExpiredOrNearExpiry(token: string, skewMs = 15000): boolean {
  const expMs = getJwtExpMs(token)
  if (!expMs) return false
  return Date.now() + skewMs >= expMs
}

export class WSClient {
  private ws?: WebSocket
  private url = ''
  private status: WSStatus = 'idle'
  private emitter = new Emitter()
  private connecting = false
  private manualClose = false
  private lastFailureAt = 0
  private cooldownMs = 5000
  private reconnectTimer?: number
  private reconnectAttempts = 0
  private refreshTried = false

  constructor(private opts: WSOptions) {}

  getStatus() {
    return this.status
  }

  on(event: string, cb: Handler) {
    return this.emitter.on(event, cb)
  }

  private setStatus(s: WSStatus) {
    this.status = s
    this.emitter.emit('status', s)
  }

  private scheduleReconnect() {
    if (this.manualClose) return
    if (this.reconnectTimer) return
    const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(5, this.reconnectAttempts)))
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined
      void this.connect()
    }, delay)
    this.reconnectAttempts += 1
  }

  private async maybeRefreshToken(): Promise<void> {
    if (this.refreshTried) return
    const refresh = getRefreshToken()
    if (!refresh) return
    this.refreshTried = true
    try {
      const { data } = await publicApi.post('/api/auth/refresh/', { refresh })
      if (data?.access) setToken(data.access)
    } catch {
      // ignore refresh failure; user may need to re-auth
    }
  }

  async connect() {
    if (this.connecting || this.status === 'open' || this.status === 'connecting') return
    if (this.lastFailureAt && Date.now() - this.lastFailureAt < this.cooldownMs) return
    this.manualClose = false
    this.connecting = true
    this.setStatus('connecting')

    let token = getToken()
    if (token && isJwtExpiredOrNearExpiry(token)) {
      await this.maybeRefreshToken()
      token = getToken()
    }
    if (!token) {
      await this.maybeRefreshToken()
      token = getToken()
    }
    if (!token) {
      this.connecting = false
      this.setStatus('closed')
      this.emitter.emit('close', { code: 4001, reason: 'auth-missing' })
      return
    }
    const base = stripTrailingSlash(toWsBaseFromLocation()) || ''
    const path = cleanPath(this.opts.path)
    const useQuery = this.opts.authAsQuery ?? true
    const tokenQs = token ? `?token=${encodeURIComponent(token)}` : ''
    this.url = useQuery ? `${base}${path}${tokenQs}` : `${base}${path}`
    const protocols = useQuery ? undefined : (['jwt', token] as string[])

    this.ws = new WebSocket(this.url, protocols as any)

    this.ws.onopen = () => {
      this.connecting = false
      this.refreshTried = false
      this.reconnectAttempts = 0
      this.setStatus('open')
      this.emitter.emit('open', { url: this.url })
    }

    this.ws.onmessage = (ev) => {
      let data: any = ev.data
      try {
        data = JSON.parse(ev.data as any)
      } catch {}
      if (data && typeof data.type === 'string') this.emitter.emit(data.type, data)
      this.emitter.emit('message', data)
    }

    this.ws.onerror = (ev: any) => {
      this.lastFailureAt = Date.now()
      this.emitter.emit('error', ev)
    }

    this.ws.onclose = (ev) => {
      this.connecting = false
      this.setStatus('closed')
      this.emitter.emit('close', { code: ev.code, reason: ev.reason })
      if (!this.manualClose) this.lastFailureAt = Date.now()
      // If the access token is stale, refresh and reconnect.
      if (!this.manualClose) {
        void this.maybeRefreshToken().finally(() => this.scheduleReconnect())
      }
    }
  }

  close() {
    this.manualClose = true
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }
    if (this.ws && this.ws.readyState === 1) {
      this.setStatus('closing')
      try {
        this.ws.close(1000, 'client-close')
      } catch {}
    } else {
      this.setStatus('closed')
    }
  }

  send(obj: any) {
    const payload = typeof obj === 'string' ? obj : JSON.stringify(obj)
    if (this.ws && this.ws.readyState === 1 && this.status === 'open') {
      try {
        this.ws.send(payload)
      } catch {}
    }
  }
}
